import type { CSSProperties } from 'react';

export type IconName =
  | 'list'
  | 'timeline'
  | 'today'
  | 'profile'
  | 'leaps'
  | 'growth'
  | 'journey'
  | 'play'
  | 'sing'
  | 'music'
  | 'read'
  | 'explore'
  | 'activities'
  | 'switch'
  | 'edit'
  | 'signout'
  | 'plus'
  | 'refresh'
  | 'arrow'
  | 'check'
  | 'chevron'
  | 'down'
  | 'filter'
  | 'search'
  | 'close'
  | 'trash'
  | 'calendar'
  | 'trophy'
  | 'photos'
  | 'lock'
  | 'mail'
  | 'settings'
  | 'sparkles'
  | 'send'
  | 'attach'
  | 'camera'
  | 'bell'
  | 'copy';

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  active?: boolean;
  style?: CSSProperties;
}

export function Icon({ name, size = 22, color = 'currentColor', active = false, style }: IconProps) {
  const sw = active ? 1.8 : 1.5;
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: color,
    strokeWidth: sw,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style,
  };
  switch (name) {
    case 'list':
      return (
        <svg width={size} height={size} viewBox="0 0 154 155" fill="none" style={style}>
          <path d="M106.407 96.8913C111.542 102.976 114.23 109.624 114.119 117.272C113.966 127.809 108.553 135.741 100.947 142.254C92.0832 149.843 81.3711 153.044 69.9069 153.943C67.396 154.087 64.8793 154.095 62.3675 153.968C61.2825 153.947 60.2075 153.756 59.1817 153.401C55.071 151.912 54.4462 148.867 57.7062 146.002C60.9065 143.191 64.3602 140.658 67.443 137.729C69.8054 135.468 71.9448 132.984 73.8318 130.313C75.8297 127.501 76.0056 124.257 74.7671 120.518C72.7928 121.437 70.9019 122.169 69.1639 123.165C65.5199 125.253 63.4643 125.166 60.3381 122.422C57.3006 119.755 54.3156 117.028 51.2938 114.343C50.8442 113.992 50.3743 113.668 49.8866 113.373C48.0535 115.067 46.3496 116.757 44.523 118.301C42.1025 120.348 39.7016 120.401 38.131 118.589C36.6588 116.893 36.901 114.651 39.0282 112.471C40.5377 110.923 42.2626 109.584 43.5372 108.461C40.1098 104.278 36.6555 100.848 34.1719 96.8237C31.2696 92.1178 34.8565 87.9231 37.0979 83.3189C32.8192 83.0504 29.3006 83.8341 26.4724 86.0033C23.7734 88.1917 21.3394 90.6881 19.2199 93.4416C17.1669 95.975 15.57 98.8767 13.7474 101.599C12.0291 104.166 10.2688 106.864 6.65102 106.491C4.14644 106.233 0.654067 101.721 0.422379 98.33C-0.102691 90.599 0.881819 83.0753 4.49168 76.0867C11.1049 63.2881 21.523 54.9657 35.0947 50.5131C40.7812 48.6478 46.5098 49.2418 52.3098 51.0087C52.6314 50.6385 53.0088 50.2697 53.3107 49.8476C68.9736 27.9581 90.3183 14.0634 115.597 5.81196C124.206 3.00217 132.916 0.444402 142.093 0.21009C144.385 0.151676 146.679 -0.0235928 148.97 0.00266074C151.958 0.036134 153.448 1.09614 153.327 4.01488C153.172 9.66322 152.644 15.2951 151.744 20.8736C148.056 41.8521 137.967 59.8443 124.863 76.254C119.763 82.6415 114.02 88.5177 108.563 94.621C107.918 95.3403 107.232 96.0243 106.407 96.8913ZM55.4163 106.628C58.8292 109.34 61.3148 111.468 63.9867 113.323C64.4408 113.537 64.9369 113.646 65.4389 113.644C65.9408 113.641 66.4358 113.527 66.8877 113.308C71.1231 111.063 75.4543 108.915 79.3969 106.215C93.4392 96.5986 106.143 85.4257 116.822 72.1336C122.616 64.9224 127.917 57.3167 133.55 49.7426L100.91 19.4316C99.8323 19.9355 98.7827 20.4966 97.7652 21.1125C91.3987 25.3374 84.9081 29.3961 78.7602 33.9196C71.8687 38.9892 66.0371 45.1843 60.8311 52.0129C52.1969 63.3367 47.1136 76.5599 40.7248 89.0828C40.0895 90.3299 40.4459 92.8417 41.3464 93.9568C43.6901 96.8598 46.5925 99.3126 49.7416 102.406C54.6641 97.3035 58.9959 92.5352 63.6454 88.0938C66.324 85.689 69.196 83.5087 72.2323 81.575C73.8285 80.4907 75.8664 80.1212 77.3268 81.7864C78.8068 83.4745 77.5322 85.1277 76.4919 86.4844C75.2622 88.0169 73.9298 89.4642 72.504 90.8162C69.5505 93.7356 66.5904 96.6465 63.5476 99.4701C60.9951 101.843 58.3212 104.081 55.4163 106.628ZM147.141 6.3889C133.175 6.41712 120.848 11.6094 108.28 15.8349L108.056 16.7045L136.962 41.0731C141.915 33.2233 145.713 20.2816 147.141 6.3889ZM77.9707 141.64C89.7309 139.425 98.8422 133.46 103.962 122.528C107.375 115.232 104.991 108.423 100.968 101.949C94.1966 107.118 87.7199 112.064 81.2445 117.008C86.3449 126.009 83.7327 133.936 77.9707 141.64ZM48.698 56.4944C31.8839 53.7377 8.64104 76.4733 10.1486 92.0817C17.0146 81.239 25.8253 74.7064 39.084 77.0567L48.698 56.4944Z" fill={color} />
          <path d="M14.6051 140.579C15.1033 139.678 15.6382 138.038 16.7054 136.898C20.7301 132.601 24.9024 128.441 29.0774 124.288C30.3034 123.068 31.8543 122.299 33.4499 123.601C35.1327 124.974 34.3963 126.615 33.402 127.973C29.989 132.633 26.5682 137.293 22.9616 141.802C21.8888 143.034 20.5253 143.979 18.9947 144.55C16.7225 145.446 14.5257 143.644 14.6051 140.579Z" fill={color} />
          <path d="M51.9876 123.87C54.4075 123.936 56.1665 126.118 55.0901 127.808C52.3487 132.062 49.3371 136.136 46.074 140.004C44.905 141.404 42.8061 141.206 41.3214 139.866C40.6426 139.292 40.1985 138.488 40.0735 137.607C39.9485 136.727 40.1513 135.831 40.6434 135.09C41.5342 133.607 42.5933 132.232 43.7998 130.991C45.587 129.088 47.4753 127.273 49.4167 125.529C50.22 124.897 51.0807 124.342 51.9876 123.87Z" fill={color} />
          <path d="M30.3682 105.409C29.9062 106.43 29.3605 107.411 28.7366 108.341C25.689 112.145 22.5946 115.911 19.4534 119.638C18.8631 120.274 18.1765 120.813 17.4187 121.234C15.8501 122.199 14.2552 122.197 12.9904 120.769C11.6836 119.294 11.9737 117.656 13.2838 116.387C17.5723 112.23 21.91 108.121 26.3337 104.109C26.912 103.584 28.1433 103.499 28.9893 103.655C29.4973 103.749 29.8465 104.705 30.3682 105.409Z" fill={color} />
          <path d="M99.4671 35.7334C103.729 35.6749 107.015 40.1649 110.313 40.1649C113.732 43.0121 116.286 46.049 117.548 50.2935C120.461 60.111 114.697 67.338 105.35 68.6599C97.775 69.731 91.3495 66.6114 86.6757 60.6814C82.672 55.6 82.5638 49.6871 85.3407 44.0052C88.1407 38.2721 93.1846 35.7944 99.4671 35.7334ZM90.4707 51.4625C91.5654 53.3514 92.3511 55.7595 93.9821 57.2317C95.6715 58.7564 98.1281 59.5118 100.344 60.3545C103.255 61.4624 105.775 60.2889 107.753 58.2294C109.668 56.2374 109.739 53.7354 108.803 51.1869C107.329 47.1688 103.891 45.1524 100.438 43.3337C97.3373 41.7008 94.3949 42.6774 92.6274 45.7353C91.7262 47.2915 91.3232 49.1358 90.4707 51.4625Z" fill={color} />
        </svg>
      );
    case 'timeline':
      return (
        <svg width={size} height={size} viewBox="0 0 157 148" fill="none" style={style}>
          <g clipPath="url(#clip_calendar)">
            <path d="M38.6174 11.6714C38.6174 9.22676 38.5662 6.98592 38.6303 4.74777C38.7093 1.9877 39.4323 0.780865 40.912 0.682198C42.3918 0.583532 43.2106 1.63988 43.6307 4.4324C43.9544 6.55568 44.1187 8.70155 44.4074 11.3357L111.435 10.7582C111.572 9.35087 111.67 8.0856 111.824 6.8276C111.983 5.54626 112.142 4.26041 112.406 2.99787C112.727 1.46659 113.641 0.46954 115.293 0.45526C117.088 0.439032 118.118 1.51198 118.405 3.16205C118.737 5.07307 118.849 7.02292 119.052 8.95601C119.108 9.4792 119.155 10.0036 119.236 10.8384C122.4 11.0026 125.478 11.2513 128.558 11.3065C142.155 11.5493 149.818 19.9339 152.541 31.7298C153.912 37.6686 154.21 43.9041 154.527 50.0292C155.266 64.2703 155.804 78.5256 156.261 92.7758C156.52 100.76 156.459 108.759 156.526 116.751C156.664 132.909 144.437 141.555 133.689 144.484C129.955 145.535 126.108 146.134 122.232 146.269C99.607 146.824 76.9766 147.444 54.3475 147.459C42.2785 147.468 30.2892 145.546 18.483 142.984C9.42075 141.019 2.43696 132.588 1.35467 122.835C1.02216 119.612 0.884885 116.371 0.943625 113.131C1.06273 102.003 1.40001 90.8771 1.44791 79.7492C1.50422 66.7862 1.37732 53.822 1.25369 40.859C1.22003 37.3038 0.990901 33.8485 2.27774 30.338C5.86768 20.5467 12.9039 15.137 22.9203 13.4298C26.9458 12.743 31.0523 12.5335 35.1213 12.0986C36.1777 11.9857 37.2205 11.8427 38.6174 11.6714ZM146.224 49.0038C123.214 49.7588 100.719 50.5615 78.2195 51.2177C55.6284 51.8792 33.0291 52.3998 10.2654 52.9867C10.2421 53.2612 10.1618 53.7787 10.1612 54.296C10.1396 71.5858 10.1259 88.8759 10.1198 106.166C10.1198 110.809 10.0959 115.457 10.3076 120.093C10.4437 123.359 11.6846 126.48 13.8258 128.945C15.9671 131.409 18.8811 133.069 22.0884 133.651C35.1639 135.873 48.2304 137.935 61.5784 137.574C73.7524 137.244 85.9379 137.36 98.115 137.112C107.807 136.917 117.497 136.538 127.179 136.098C131.402 135.878 135.481 134.485 138.96 132.073C143.656 128.901 146.812 124.747 146.914 118.766C146.994 114.125 147.293 109.484 147.221 104.844C146.948 87.2349 146.561 69.6274 146.222 52.0197C146.206 50.9564 146.226 49.8931 146.226 49.0038H146.224ZM9.40004 44.9297C54.9669 42.489 99.8116 40.8995 144.935 39.8219C144.676 37.5208 144.641 35.3429 144.158 33.2702C142.664 26.8848 139.868 21.4187 132.926 19.522C128.521 18.3185 124.004 18.6662 119.332 18.7733C119.228 19.8029 119.161 20.644 119.052 21.4795C118.962 22.3372 118.824 23.1894 118.64 24.032C117.982 26.7102 116.634 27.9266 114.556 27.7818C112.464 27.6377 111.268 26.2312 111.079 23.4744C110.971 21.9009 111.06 20.3138 111.06 18.8111H44.9162C44.9162 20.3462 44.9453 21.5109 44.9091 22.6728C44.908 24.2893 44.8215 25.9044 44.6502 27.5118C44.5905 28.2807 44.2425 28.9985 43.6763 29.5206C43.1102 30.0427 42.3679 30.3303 41.5988 30.3257C39.7863 30.4348 38.7571 29.2872 38.3558 27.7254C38.1206 26.4554 38.0189 25.1641 38.0522 23.8728C37.9939 22.4181 37.994 20.9608 37.9623 19.0998C33.0628 19.5146 28.5809 19.4471 24.3041 20.3571C17.1909 21.8715 11.3419 25.4107 10.0273 33.3618C9.42204 37.0365 9.59682 40.8415 9.40004 44.9297Z" fill={color} />
          </g>
          <path d="M40.5799 123.022C39.6297 123.064 37.8043 123.217 35.9789 123.21C32.0219 123.201 29.6088 121.406 28.8728 117.563C27.9763 113.107 28.1739 108.501 29.4488 104.139C29.6864 103.195 30.1734 102.332 30.8583 101.643C31.5433 100.953 32.4009 100.461 33.3411 100.218C38.5107 98.7093 43.9077 98.1413 49.2771 98.5392C52.1537 98.7391 54.379 100.346 55.038 103.369C56.0361 107.939 56.3042 112.557 55.0633 117.131C54.0218 120.967 51.5582 122.863 47.5805 123.01C45.5428 123.084 43.4992 123.022 40.5799 123.022ZM45.3311 107.561H36.9252L37.2812 113.172H45.3311V107.561Z" fill={color} />
          <path d="M92.565 108.474C92.565 109.574 92.5799 110.223 92.565 110.871C92.3864 117.589 90.2309 120.202 83.6005 121.36C80.2125 121.952 76.7792 122.343 73.3505 122.633C69.6324 122.948 67.1364 121.288 66.4075 117.791C65.485 113.349 64.9445 108.842 65.9143 104.296C66.5965 101.101 68.4802 98.9735 71.6597 98.4984C75.9119 97.8337 80.1989 97.4156 84.4996 97.2475C89.3013 97.095 91.3889 99.0449 92.1378 103.765C92.4013 105.466 92.4511 107.202 92.565 108.474ZM82.928 106.437H73.6567C73.9674 108.86 74.2457 111.023 74.5331 113.264C76.6187 113.06 78.3237 112.908 80.0255 112.724C83.1066 112.391 83.4393 112.01 83.2931 108.865C83.2568 108.133 83.0762 107.409 82.9254 106.437H82.928Z" fill={color} />
          <path d="M115.165 121.917C113.124 122.01 111.08 121.998 109.04 121.88C105.343 121.49 103.188 119.533 102.966 115.908C102.703 111.925 102.752 107.928 103.111 103.953C103.5 99.8881 105.338 98.2296 109.408 97.909C113.371 97.5961 117.366 97.5072 121.342 97.5896C125.539 97.6772 129.492 100.381 129.181 105.915C129.029 108.608 128.982 111.317 128.636 113.987C127.974 119.09 125.479 121.329 120.323 121.718C118.605 121.848 116.885 121.938 115.166 122.046L115.165 121.917ZM111.181 105.439C111.31 108.201 111.408 110.296 111.504 112.315H119.039C119.233 110.227 119.392 108.442 119.582 106.364L111.181 105.439Z" fill={color} />
          <path d="M114.832 86.8755C113.007 86.9787 111.181 87.1813 109.357 87.1637C104.859 87.1209 102.516 84.9905 102.292 80.5304C102.118 77.0842 102.118 73.6321 102.292 70.1866C102.513 65.8732 104.062 64.0263 108.317 63.5803C113.018 63.0819 117.744 62.8762 122.47 62.9648C126.857 63.0538 129.194 65.5259 129.171 70.0133C129.123 73.6711 128.748 77.3172 128.05 80.9075C127.35 84.6458 125.106 86.3835 121.275 86.6535C119.139 86.8048 116.979 86.6808 114.829 86.6808L114.832 86.8755ZM110.3 78.1293H119.521C119.771 75.9002 119.992 73.9216 120.273 71.3998L110.3 70.959V78.1293Z" fill={color} />
          <path d="M63.3293 75.4114C63.5449 73.2693 63.6808 71.0026 64.0226 68.7644C64.411 66.2328 65.6701 64.2777 68.3597 63.7298C73.1173 62.7607 77.9009 61.938 82.7868 62.5203C87.0531 63.0279 89.3679 64.8488 89.9595 69.1156C90.3544 72.3093 90.3019 75.5432 89.8048 78.7226C89.3064 82.1629 86.8758 84.2297 83.4256 84.6822C79.2622 85.2274 75.0754 85.6611 70.8861 85.9194C66.8366 86.17 64.3851 84.098 63.6854 80.0585C63.4303 78.5791 63.4472 77.0511 63.3293 75.4114ZM80.8138 71.1434L71.156 70.5028C71.3068 72.9116 71.4227 74.7688 71.556 76.8947L80.8125 75.9619L80.8138 71.1434Z" fill={color} />
          <defs>
            <clipPath id="clip_calendar">
              <rect width="156" height="148" fill="white" transform="translate(0.777344)" />
            </clipPath>
          </defs>
        </svg>
      );
    case 'today':
      return (
        <svg width={size} height={size} viewBox="0 0 158 160" fill="none" style={style}>
          <g clipPath="url(#clip_home)">
            <path d="M151.886 66.6515C150.949 65.8042 149.98 64.9277 149.056 64.0429C141.33 56.6493 133.607 49.2525 125.886 41.8532C112.662 29.1867 99.4339 16.5271 86.2013 3.87434C81.4332 -0.681452 79.358 -0.64129 74.5155 4.10365L71.0912 7.45858C54.2522 23.9548 36.8401 41.0132 19.6554 57.7297C16.005 61.2801 12.1885 64.7904 8.49739 68.1815C6.71115 69.8223 4.92858 71.4683 3.14967 73.1188C2.38276 73.8314 0.0484318 76.0066 2.05713 78.3451C3.40229 79.9114 5.19714 79.8162 7.39128 78.0633C8.82108 76.9193 10.2496 75.7313 11.6316 74.5783C12.5232 73.8346 13.4167 73.0949 14.3122 72.3571C14.7037 72.0332 15.1114 71.7404 15.7304 71.2934L15.9332 71.1464C15.9332 72.5268 15.9423 73.867 15.9488 75.1729C15.9656 78.746 15.9811 82.1216 15.9171 85.4758C15.7943 91.8557 15.6561 98.2349 15.5023 104.615C15.3124 112.937 15.1114 121.543 14.9757 130.008C14.8866 135.384 14.8355 141.095 15.2665 146.655C15.7317 152.657 18.0272 154.832 24.3647 155.271C28.2141 155.539 32.1282 155.842 35.9136 156.137C43.4011 156.72 51.1432 157.32 58.7742 157.671C77.001 158.509 95.5154 159.145 113.804 159.562C114.45 159.576 115.104 159.584 115.765 159.584C121.112 159.543 126.452 159.157 131.75 158.427C139.692 157.395 142.568 154.615 143.585 146.983C144.861 137.412 145.721 127.332 146.214 116.166C146.65 106.29 146.813 96.5948 146.986 86.3315C147.051 82.4532 147.123 78.5433 147.198 74.6016C147.872 75.2383 148.454 75.7883 149.067 76.3188C151.498 78.4254 154.113 78.6068 155.889 76.7917C156.29 76.3946 156.606 75.9205 156.82 75.3984C157.034 74.8756 157.14 74.3153 157.132 73.7511C157.037 72.1828 156.326 70.7169 155.154 69.6746C154.087 68.6427 152.968 67.6277 151.886 66.6515ZM62.1442 96.701L92.606 93.8158L93.5616 151.767L60.9541 150.51L62.1442 96.701ZM101.62 102.118C101.612 99.1814 101.454 96.2048 101.302 93.3268C101.264 92.5928 101.226 91.8583 101.189 91.1243C101.086 89.0625 100.52 87.6102 99.4585 86.6832C98.3789 85.7407 96.8147 85.3702 94.6774 85.5639C82.3119 86.6592 70.9762 87.5953 60.0231 88.4244C55.3784 88.7762 53.8129 90.4675 53.8168 95.1282C53.8362 115.043 53.831 130.32 53.7987 144.664C53.7722 145.92 53.6816 147.174 53.5273 148.421C53.4795 148.879 53.4298 149.348 53.3839 149.832L22.5996 147.912C22.544 147.435 22.4833 146.97 22.4226 146.514C22.233 145.3 22.125 144.075 22.0995 142.846C22.1305 137.491 22.1932 132.047 22.254 126.781C22.3257 120.498 22.4 114.001 22.4219 107.611C22.4433 101.591 22.4271 95.4715 22.4122 89.5535C22.3942 82.3982 22.3754 74.9993 22.4206 67.7235C22.4185 66.417 22.8789 65.1525 23.7199 64.1542C37.6179 50.7745 51.8249 37.2502 65.5652 24.1717L71.977 18.0672C74.0296 16.1128 76.0958 14.1741 78.206 12.1932L80.8162 9.74069C82.6718 11.5234 84.5222 13.2968 86.3667 15.0614C90.6348 19.1475 95.0483 23.3738 99.3318 27.5513C104.786 32.8716 110.232 38.1994 115.67 43.535C122.602 50.3262 129.769 57.3473 136.845 64.2273C138.3 65.6435 138.964 66.7758 138.557 69.0366C138.316 70.8581 138.321 72.7036 138.575 74.5238C138.708 75.7002 138.773 76.8837 138.769 78.0678L138.699 80.7885C138.418 91.7624 138.128 103.109 137.597 114.254C137.279 120.91 136.757 127.673 136.257 134.213C136.03 137.162 135.805 140.112 135.594 143.061L135.584 143.195C135.565 143.47 135.545 143.746 135.51 144.019C134.77 149.882 134.258 150.408 128.672 151.052C119.701 152.092 110.658 152.374 101.64 151.894L101.644 139.013C101.649 126.899 101.655 114.374 101.621 102.118H101.62Z" fill={color} />
          </g>
          <defs>
            <clipPath id="clip_home">
              <rect width="157" height="160" fill="white" transform="translate(0.777344)" />
            </clipPath>
          </defs>
        </svg>
      );
    case 'profile':
      return (
        <svg {...common}>
          <circle cx="12" cy="9" r="3.6" />
          <path d="M5 20c1.6-3.5 4.2-5.2 7-5.2s5.4 1.7 7 5.2" />
        </svg>
      );
    case 'leaps':
      return (
        <svg width={size} height={size} viewBox="0 0 157 150" fill="none" style={style}>
          <g clipPath="url(#clip_layer)">
            <path d="M3.85747 101.836C2.04502 103.067 1.14325 104.225 1.01509 105.481C0.894688 106.668 1.48249 107.912 2.81334 109.284C5.04277 111.675 7.57088 113.766 10.3349 115.505C20.9714 121.903 31.6915 128.157 42.1991 134.093C48.3628 137.575 54.707 140.969 60.8415 144.252C63.4889 145.668 66.1341 147.087 68.7777 148.511C70.1125 149.325 71.6271 149.795 73.1878 149.881C74.1885 149.879 75.1711 149.612 76.0353 149.106C80.6959 146.449 85.7526 143.508 91.4955 140.112L99.1809 135.566C116.529 125.307 134.467 114.703 152.077 104.212C154.642 102.684 156.112 100.427 156.111 98.0195C156.111 95.5662 154.593 93.261 151.952 91.6942C149.682 90.3468 147.351 89.0318 144.883 87.6429C144 87.1448 143.092 86.6325 142.15 86.0994C143.236 85.3026 144.295 84.5286 145.328 83.7766C148.102 81.75 150.722 79.8364 153.319 77.8974C155.615 76.1831 156.808 74.0091 156.675 71.776C156.54 69.4825 155.033 67.3961 152.436 65.9C150.151 64.5837 147.874 63.255 145.51 61.8738L143.05 60.4394C143.904 59.7901 144.749 59.151 145.584 58.522C148.063 56.6512 150.405 54.8856 152.724 53.0161C154.65 51.4642 155.617 49.5602 155.445 47.6544C155.268 45.6842 153.908 43.9135 151.614 42.6681C148.521 40.9882 145.74 39.4576 143.036 37.7381L141.861 36.9895C122.483 24.6583 102.483 11.9077 81.2972 1.79471C76.3939 -0.550739 72.1146 -0.291026 67.8333 2.61092C62.4118 6.2856 57.0178 9.83813 51.5093 13.455C45.712 17.2646 39.9127 21.0685 34.1111 24.8668C24.6961 31.0356 14.96 37.4181 5.40256 43.722C2.83017 45.42 1.56073 47.0408 1.40732 48.8252C1.25844 50.5629 2.22817 52.3895 4.37139 54.4096C6.85601 56.6621 9.46302 58.7749 12.1805 60.7383C13.2323 61.5285 14.2836 62.3186 15.3199 63.1245C16.008 63.6596 16.7316 64.1505 17.4987 64.6707L17.887 64.9357C16.6054 65.9046 15.3444 66.852 14.1042 67.7786C10.7544 70.2883 7.58979 72.6598 4.4847 75.1637C2.30588 76.9169 1.29603 78.4838 1.30444 80.087C1.31351 81.702 2.35696 83.2721 4.58821 85.0312C6.33593 86.4084 8.22084 87.6474 10.0436 88.8461C11.1441 89.5688 12.2794 90.3162 13.3675 91.0825C14.1016 91.6227 14.8058 92.2026 15.4772 92.8195C15.6312 92.9565 15.7872 93.0955 15.9478 93.2357C14.815 94.0461 13.7037 94.8461 12.6141 95.6344C9.6106 97.8071 6.77227 99.8559 3.85747 101.836ZM30.6557 101.044C36.2873 104.187 42.113 107.437 47.8636 110.553C50.8412 112.165 53.892 113.74 56.8456 115.264C60.6647 117.234 64.6134 119.271 68.4185 121.401C72.2641 123.552 76.146 123.69 81.0079 121.849C93.9067 116.965 105.541 110.059 115.214 103.949C119.848 101.021 124.34 98.0656 129.101 94.9377C131.114 93.6143 133.149 92.2799 135.205 90.9338L146.32 97.787C143.49 99.5351 140.683 101.273 137.898 103C130.915 107.327 124.321 111.414 117.547 115.525C111.631 119.117 105.6 122.742 99.7673 126.247C92.0644 130.877 84.0955 135.662 76.2955 140.436C74.8151 141.339 73.8319 141.112 72.4246 140.337C67.4831 137.61 62.5391 134.887 57.5932 132.169C47.762 126.76 37.5967 121.168 27.6231 115.624C23.6933 113.44 19.8316 111.168 15.7433 108.763C14.1121 107.804 12.451 106.827 10.7441 105.831L23.4532 97.0318C25.8617 98.3695 28.2617 99.7072 30.6532 101.044H30.6557ZM136.212 54.6355C129.398 59.4459 122.961 63.9902 116.226 68.2877C108.417 73.2721 100.323 78.1065 92.4962 82.7818C87.9864 85.4753 83.3233 88.2611 78.7624 91.0455C76.9596 92.1494 75.6508 92.1234 73.7684 90.9552C64.1887 85.0136 54.424 79.0201 44.9792 73.2279C37.0731 68.3766 28.8984 63.3577 20.8763 58.3993C18.2871 56.7986 15.8274 55.0642 13.2246 53.2265C12.2752 52.5577 11.3148 51.8841 10.3435 51.2057C12.5581 49.6906 14.7359 48.1947 16.8767 46.7181C22.2105 43.0466 27.2491 39.5752 32.412 36.1557C38.4474 32.1551 44.6149 28.1331 50.5798 24.2441C56.3861 20.4577 62.3892 16.5427 68.2663 12.6505L68.4541 12.5259C76.502 7.19469 76.5026 7.19664 85.1221 11.7817C102.287 20.9109 119.822 31.6414 141.979 46.5751C142.601 46.994 143.204 47.4765 143.968 48.0882C144.198 48.272 144.442 48.4674 144.706 48.6765C141.82 50.6759 138.972 52.6855 136.211 54.6355H136.212ZM25.1886 69.2513C26.863 70.3604 28.5403 71.4799 30.2202 72.6091C34.2115 75.2818 38.338 78.0468 42.4931 80.652C48.9745 84.7182 55.6073 88.7779 62.0234 92.7039C65.6914 94.9494 69.3596 97.1994 73.0273 99.4539C76.3156 101.484 79.2847 100.222 81.2325 99.0974C83.7718 97.6305 86.3138 96.1675 88.859 94.7091C96.8364 90.1286 105.086 85.3916 113.078 80.5247C120.001 76.3091 126.576 71.7636 133.84 66.6864C135.726 65.3682 136.941 65.3468 138.655 66.5929C140.568 67.9844 142.576 69.2598 144.701 70.6097C145.393 71.0487 146.103 71.502 146.83 71.9675C145.351 73.0214 143.914 74.0656 142.5 75.0929C138.927 77.6903 135.552 80.1422 132.013 82.3786C116.294 92.3247 102.452 100.895 89.6953 108.58C87.167 110.01 84.5337 111.245 81.8183 112.275C80.3554 112.873 78.8413 113.492 77.3856 114.165C76.3887 114.658 75.2838 114.889 74.1743 114.836C73.0642 114.782 71.9864 114.446 71.042 113.86C61.8293 108.877 52.6082 103.909 43.3785 98.9578C34.2826 94.0656 25.1911 89.1688 16.1038 84.2669C14.4434 83.3688 12.8304 82.2942 11.1195 81.1572C10.5266 80.7624 9.91742 80.3572 9.28307 79.9429L25.1886 69.2513Z" fill={color} />
          </g>
          <defs>
            <clipPath id="clip_layer">
              <rect width="156" height="150" fill="white" transform="translate(0.777344)" />
            </clipPath>
          </defs>
        </svg>
      );
    case 'growth':
      return (
        <svg {...common}>
          <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="M7 21h10" />
          <path d="M12 3v18" />
          <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
        </svg>
      );
    case 'switch':
      return (
        <svg {...common}>
          <path d="M4 8h13l-3-3" />
          <path d="M20 16H7l3 3" />
        </svg>
      );
    case 'edit':
      return (
        <svg {...common}>
          <path d="M4 20h4l10-10-4-4L4 16v4z" />
          <path d="M14 6l4 4" />
        </svg>
      );
    case 'signout':
      return (
        <svg {...common}>
          <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
          <path d="M16 8l4 4-4 4" />
          <path d="M20 12H10" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...common}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M4 12a8 8 0 0 1 13.7-5.6L20 9" />
          <path d="M20 4v5h-5" />
          <path d="M20 12a8 8 0 0 1-13.7 5.6L4 15" />
          <path d="M4 20v-5h5" />
        </svg>
      );
    case 'arrow':
      return (
        <svg {...common}>
          <path d="M5 12h14M14 6l6 6-6 6" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      );
    case 'chevron':
      return (
        <svg {...common}>
          <path d="M9 6l6 6-6 6" />
        </svg>
      );
    case 'down':
      return (
        <svg {...common}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    case 'filter':
      return (
        <svg {...common}>
          <path d="M4 6h16" />
          <path d="M7 12h10" />
          <path d="M10 18h4" />
        </svg>
      );
    case 'search':
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" />
        </svg>
      );
    case 'close':
      return (
        <svg {...common}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...common}>
          <path d="M4 7h16" />
          <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
          <path d="M7 7l1 13h8l1-13" />
          <path d="M10 11v5M14 11v5" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
          <path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
      );
    case 'trophy':
      return (
        <svg {...common}>
          <path d="M7 4h10v4a5 5 0 0 1-10 0V4z" />
          <path d="M7 6H4.5a2.5 2.5 0 0 0 2.7 3.5M17 6h2.5a2.5 2.5 0 0 1-2.7 3.5" />
          <path d="M12 13v3M9 20h6M10 20c0-1.5.7-2.4 2-2.4s2 .9 2 2.4" />
        </svg>
      );
    case 'journey':
      return (
        <svg {...common}>
          <circle cx="12" cy="5" r="2" fill={active ? color : 'none'} />
          <path d="M12 7C6 10 18 13 12 16.5" />
          <circle cx="12" cy="19" r="2" fill={active ? color : 'none'} />
        </svg>
      );
    case 'play':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 3.5c3 2.6 3 14.4 0 17M3.8 9.2c4.4 2 12 2 16.4 0M3.8 14.8c4.4-2 12-2 16.4 0" />
        </svg>
      );
    case 'sing':
      return (
        <svg {...common}>
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
          <path d="M12 18v4" />
        </svg>
      );
    case 'music':
      return (
        <svg {...common}>
          <path d="M9 17V6l9-1.8V15" />
          <circle cx="6.5" cy="17" r="2.4" />
          <circle cx="15.5" cy="15" r="2.4" />
        </svg>
      );
    case 'read':
      return (
        <svg {...common}>
          <path d="M12 6.5C9.8 5.3 7.3 5.2 5 6v12c2.3-0.8 4.8-0.7 7 0.5 2.2-1.2 4.7-1.3 7-0.5V6c-2.3-0.8-4.8-0.7-7 0.5z" />
          <path d="M12 6.5V19" />
        </svg>
      );
    case 'explore':
      return (
        <svg {...common}>
          <path d="M12 21c4.2-4.7 6.3-8.1 6.3-11.3a6.3 6.3 0 1 0-12.6 0C5.7 12.9 7.8 16.3 12 21z" />
          <circle cx="12" cy="9.6" r="2.3" />
        </svg>
      );
    case 'activities':
      return (
        <svg width={size} height={size} viewBox="0 0 160 155" fill="none" style={style}>
          <path d="M78.6337 112.149C68.465 112.2 60.044 110.437 52.9637 104.775C50.5781 102.868 48.7417 100.179 46.9811 97.6218C45.9695 96.1517 46.1096 94.2009 47.9291 93.2048C49.473 92.3597 51.0372 92.8371 52.1153 94.2923C58.5773 103.015 67.9124 104.934 77.8956 104.865C81.6802 104.84 85.4851 103.972 89.2331 103.245C95.8312 101.966 101.133 98.6679 104.732 92.8275C105.588 91.5023 106.59 90.2771 107.719 89.1745C109.149 87.72 110.872 86.7874 112.822 88.1932C114.555 89.4425 114.521 91.1815 113.803 93.0477C111.736 98.5894 107.816 103.246 102.708 106.228C94.9015 110.867 86.2177 111.916 78.6337 112.149Z" fill={color} />
          <path d="M58.658 46.9225L54.2952 45.9549C44.5144 43.788 36.0399 45.7112 29.3552 53.6812C28.6597 54.51 27.1551 54.6602 26.0263 55.1254C25.6992 53.8389 24.6937 52.1515 25.1724 51.3335C26.6208 48.8687 28.2751 46.2549 30.509 44.5688C39.7399 37.6118 49.9858 37.2435 60.6062 40.8445C62.3078 41.4221 63.6486 42.6098 63.1827 44.6331C62.7256 46.6307 61.1181 47.127 58.658 46.9225Z" fill={color} />
          <path d="M51.8751 56.8652C57.3688 56.9803 61.9158 59.3957 65.6076 63.5331C66.2438 64.1678 66.6721 64.9809 66.8359 65.8645C66.9213 67.0204 66.9003 68.6042 66.2123 69.2671C65.6469 69.8089 63.91 69.5834 62.9329 69.1534C61.4127 68.4844 60.099 67.3494 58.6933 66.4184C51.0693 61.3682 44.2578 63.074 39.7196 70.981C39.1217 72.0225 37.5087 73.2345 36.4998 73.1262C34.5638 72.9176 34.5442 70.9594 34.7256 69.3491C35.4224 63.1627 41.6407 57.8153 49.0202 57.0251C49.9154 56.9276 50.8201 56.9201 51.8751 56.8652Z" fill={color} />
          <path d="M112.27 35.8912C116.93 35.7287 121.291 37.1101 125.411 39.3548C126.403 39.8832 127.334 40.5189 128.187 41.2507C129.538 42.4364 130.46 43.9391 129.447 45.7199C128.344 47.6573 126.468 47.5062 124.669 46.9056C123.721 46.5478 122.819 46.078 121.982 45.506C114.616 40.8581 107.326 41.1356 100.075 45.7707C98.8842 46.6191 97.6222 47.3626 96.3032 47.9931C95.5888 48.2897 94.2176 48.3093 93.8654 47.8732C93.5999 47.473 93.4193 47.0228 93.3348 46.55C93.2502 46.0773 93.2635 45.5922 93.3738 45.1248C93.7337 44.1831 94.3359 43.353 95.1195 42.7187C99.9164 38.2455 105.434 35.5222 112.27 35.8912Z" fill={color} />
          <path d="M113.689 54.1131C117.508 53.9818 121.401 55.5399 124.687 58.5566C125.77 59.5384 126.705 60.6722 127.463 61.9227C128.873 64.3103 128.6 66.9491 127.043 68.1003C125.412 69.307 123.458 68.6292 121.308 66.1108C115.46 59.2595 109.032 58.839 102.18 64.8609C101.926 65.0836 101.689 65.3259 101.435 65.5507C100.239 66.6193 98.9008 67.9458 97.3399 66.5665C95.6471 65.07 96.4725 63.3081 97.5675 61.7391C100.821 57.0811 107.023 54.0116 113.689 54.1131Z" fill={color} />
          <path d="M3.80298 55.9748C-3.20684 83.5259 2.93151 107.831 22.0602 128.223C22.5579 128.75 23.0658 129.277 23.5737 129.785C33.8487 140.06 46.6474 146.404 56.7891 150.819C61.1411 152.719 83.4754 155.028 89.5176 154.128C125.429 148.765 149.061 128.511 157.292 94.625C158.864 87.9873 159.647 81.1873 159.624 74.3661C159.662 58.2974 152.897 39.6163 138.944 25.6251C137.508 24.4373 136.076 23.2069 134.692 22.0145C131.69 19.4339 128.587 16.762 125.341 14.4834C100.543 -2.97008 74.7775 -3.06774 48.7635 6.1875C25.211 14.5618 10.0849 31.3158 3.80298 55.9748ZM48.8644 14.4373C71.6897 4.95246 94.5915 3.27855 116.928 17.4844C121.012 20.0847 131.9 28.8849 133.615 30.2067L133.724 30.2879L133.816 30.3889C142.53 39.6299 152.505 56.5268 152.288 73.8963C152.216 81.3232 151.21 88.7114 149.296 95.8878C143.622 116.511 130.057 131.541 107.826 141.821C85.2259 152.642 52.8149 141.702 44.1914 136.01C34.6598 129.493 26.3197 121.386 19.5358 112.043C8.89649 96.9802 6.50888 75.3276 12.226 55.5339C14.9018 46.3948 19.5776 37.9656 25.9147 30.8574C32.2517 23.7493 40.0911 18.1403 48.8644 14.4373Z" fill={color} />
        </svg>
      );
    case 'photos':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="10" rx="2" ry="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M22 6l-10 7L2 6" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'sparkles':
      return (
        <svg {...common}>
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
          <path d="M18 14l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14z" />
        </svg>
      );
    case 'send':
      return (
        <svg {...common}>
          <path d="M4.5 12L20 4l-5 16-3.5-6.5L4.5 12z" />
          <path d="M11.5 13.5L20 4" />
        </svg>
      );
    case 'attach':
      return (
        <svg {...common}>
          <path d="M20 11.5l-8.4 8.4a4.5 4.5 0 0 1-6.4-6.4l8.5-8.5a3 3 0 0 1 4.3 4.3l-8.5 8.5a1.5 1.5 0 0 1-2.1-2.1l7.8-7.8" />
        </svg>
      );
    case 'camera':
      return (
        <svg {...common}>
          <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
          <circle cx="12" cy="13" r="3.2" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...common}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
          <path d="M13.7 20a2 2 0 0 1-3.4 0" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...common}>
          <rect x="9" y="9" width="11" height="11" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    default:
      return null;
  }
}

interface StatusDotProps {
  status: 'done' | 'now' | 'soon';
  color: string;
  size?: number;
}

export function StatusDot({ status, color, size = 16 }: StatusDotProps) {
  const innerSize = Math.round(size * 0.45);
  const checkSize = Math.round(size * 0.65);

  if (status === 'done') {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        <svg
          width={checkSize}
          height={checkSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
    );
  }
  if (status === 'now') {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: '#D4A845',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            width: innerSize,
            height: innerSize,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.85)',
          }}
        />
      </span>
    );
  }
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid #B8AEA1',
        background: 'transparent',
        display: 'inline-block',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    />
  );
}
